import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  Heart,
  Wallet,
  Gift,
  Smartphone,
  TrendingUp,
  Users,
  Ticket,
  Star,
  Mail,
  BarChart3
} from "lucide-react";

export function LoyaltyRewardsPage() {
  const features = [
    {
      icon: Wallet,
      title: "Digital Wallet Integration",
      description: "Create beautiful loyalty cards for Apple Wallet and Google Pay. Customers carry your brand in their pocket."
    },
    {
      icon: Gift,
      title: "Digital Coupons",
      description: "Design and distribute digital coupons with custom offers. Track redemption and measure campaign success."
    },
    {
      icon: Heart,
      title: "Reward Programs",
      description: "Build custom loyalty programs that drive repeat business. Points, stamps, tiers - your rules."
    },
    {
      icon: Smartphone,
      title: "QR Code Redemption",
      description: "Fast and secure reward redemption via QR scanning. No paper cards, no hassle."
    },
    {
      icon: Users,
      title: "Customer Segmentation",
      description: "Target specific customer groups with personalized offers. Increase engagement with relevant rewards."
    },
    {
      icon: TrendingUp,
      title: "Real-Time Analytics",
      description: "Track program performance, redemption rates, and customer lifetime value. Optimize your strategy with data."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Design Your Program",
      description: "Create loyalty cards or coupons with your branding and reward structure",
      icon: Gift
    },
    {
      number: 2,
      title: "Distribute Passes",
      description: "Share via email, QR codes, or in-person. Customers add to their digital wallet",
      icon: Wallet
    },
    {
      number: 3,
      title: "Track Engagement",
      description: "Monitor customer activity, redemptions, and program performance",
      icon: TrendingUp
    },
    {
      number: 4,
      title: "Reward & Retain",
      description: "Automatically update passes and send notifications for new rewards",
      icon: Heart
    }
  ];

  const relatedFeatures = [
    {
      title: "Reviews & Feedback",
      description: "Reward customers who leave reviews with exclusive offers",
      icon: Star,
      link: "/features/reviews"
    },
    {
      title: "Marketing & Communications",
      description: "Promote your loyalty program through email campaigns",
      icon: Mail,
      link: "/features/marketing"
    },
    {
      title: "Analytics & Insights",
      description: "Measure the impact of your loyalty program on revenue",
      icon: BarChart3,
      link: "/features/analytics"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Build Customer Loyalty with Digital Rewards"
        subtitle="Create stunning digital loyalty cards and coupons for Apple Wallet and Google Pay. Drive repeat business with rewards that customers actually use."
        primaryCTA={{ text: "Start Free Trial", link: "/sign-up" }}
        secondaryCTA={{ text: "View Examples", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Everything You Need to Reward Customers"
        subtitle="Powerful tools to create, distribute, and manage digital loyalty programs"
      >
        <FeatureGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Launch your loyalty program in minutes"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Professional Passes That Stand Out"
          description="Design eye-catching loyalty cards and coupons that match your brand perfectly. Our intuitive builder makes it easy to create professional passes without any design skills. Customers can add them to Apple Wallet or Google Pay with a single tap."
          imagePosition="left"
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="Enhance your customer engagement strategy"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Transform One-Time Customers Into Loyal Fans"
          description="Start building stronger customer relationships today with digital loyalty programs that work."
          primaryCTA={{ text: "Create Your First Pass", link: "/sign-up" }}
          secondaryCTA={{ text: "Talk to an Expert", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
